// Bottom Navigation Styles
// TikTok-inspired mobile bottom navigation

import styled, { keyframes, css } from "styled-components";
import { theme } from "../theme";

// Animations
const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`;

// Bottom Navigation Container
export const BottomNavigationContainer = styled.nav<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--bg-card);
  backdrop-filter: blur(20px);
  border-top: 1px solid var(--border-light);
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  display: flex;
  justify-content: space-around;
  align-items: center;
  min-height: 35px;
  opacity: ${({ $isVisible }) => $isVisible ? '1' : '0'};
  visibility: ${({ $isVisible }) => $isVisible ? 'visible' : 'hidden'};
  transform: translateY(${({ $isVisible }) => $isVisible ? '0' : '100%'});
  transition: all 0.3s ease;
  animation: ${css`${slideUp} 0.3s ease-out`};
  box-shadow: ${theme.shadows.medium};

  /* Always show on mobile, hide on larger screens */
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }

  /* Safe area for devices with home indicator */
  padding-bottom: calc(${theme.spacing.xs} + env(safe-area-inset-bottom));
  
  /* Ensure consistent appearance across all pages */
  * {
    box-sizing: border-box;
  }
`;

// Navigation Item
export const NavigationItem = styled.button<{ 
  $isActive: boolean; 
  $hasNotification?: boolean;
  $variant?: 'primary' | 'secondary';
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: none;
  border: none;
  color: ${({ $isActive }) => 
    $isActive ? theme.colors.primary : 'var(--text-secondary)'};
  font-size: 0.65rem;
  font-weight: ${({ $isActive }) => $isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: ${theme.borderRadius.md};
  min-width: 50px;
  position: relative;
  transform: ${({ $isActive }) => $isActive ? 'scale(1.05)' : 'scale(1)'};

  &:hover {
    color: ${theme.colors.primary};
    background: var(--bg-secondary);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Active state styling */
  ${({ $isActive }) => $isActive && css`
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: ${theme.colors.primary};
      border-radius: 50%;
    }
  `}

  /* Notification badge */
  ${({ $hasNotification }) => $hasNotification && css`
    &::after {
      content: '';
      position: absolute;
      top: 8px;
      right: 12px;
      width: 8px;
      height: 8px;
      background: ${theme.colors.error};
      border-radius: 50%;
      animation: ${pulse} 2s infinite;
    }
  `}
`;

// Navigation Icon
export const NavigationIcon = styled.span<{ $isActive: boolean }>`
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: inherit;

  svg {
    width: 100%;
    height: 100%;
  }

  ${({ $isActive }) => $isActive && css`
    animation: ${bounce} 0.6s ease;
  `}
`;

// Navigation Label
export const NavigationLabel = styled.span<{ $isActive: boolean }>`
  font-size: 0.6rem;
  font-weight: ${({ $isActive }) => $isActive ? '600' : '400'};
  text-align: center;
  line-height: 1;
  transition: all 0.2s ease;
  opacity: ${({ $isActive }) => $isActive ? '1' : '0.8'};
`;

// Special Action Button (for swipe/matching)
export const SpecialActionButton = styled.div<{ $isActive: boolean }>`
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${({ $isActive }) => 
    $isActive 
      ? theme.colors.gradients.primary
      : theme.colors.gradients.warm};
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $isActive }) => $isActive ? 'scale(1.1)' : 'scale(1)'};

  svg {
    width: 18px;
    height: 18px;
    color: white;
  }

  &:hover {
    transform: scale(1.15);
    box-shadow: ${theme.shadows.heavy};
  }

  &:active {
    transform: scale(0.95);
  }

  /* Pulse animation for active state */
  ${({ $isActive }) => $isActive && css`
    animation: ${pulse} 2s infinite;
  `}

  /* Glow effect */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${theme.colors.gradients.primary};
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 0.3;
  }
`;

// Notification Badge
export const NotificationBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background: ${theme.colors.error};
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
  line-height: 1;
  animation: ${css`${pulse} 2s infinite`};
`;

// Mobile-specific adjustments
export const MobileFeedContainer = styled.div`
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: 45px; /* Space for bottom navigation */
  }
`;

// Swipe Indicator
export const SwipeIndicator = styled.div<{ $direction: 'left' | 'right' | 'up' }>`
  position: absolute;
  top: 50%;
  ${({ $direction }) => {
    switch ($direction) {
      case 'left':
        return 'left: 20px;';
      case 'right':
        return 'right: 20px;';
      case 'up':
        return 'left: 50%; transform: translateX(-50%); top: 20px;';
      default:
        return 'left: 20px;';
    }
  }}
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 100;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  opacity: 0;
  transition: opacity 0.2s ease;

  &.visible {
    opacity: 1;
  }
`;

// Quick Actions Panel
export const QuickActionsPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 45px;
  left: 0;
  right: 0;
  background: var(--bg-card);
  backdrop-filter: blur(20px);
  border-top: 1px solid ${theme.colors.border.light};
  padding: ${theme.spacing.sm};
  transform: translateY(${({ $isOpen }) => $isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};

  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const QuickActionItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: var(--bg-secondary);
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-tertiary);
    transform: translateY(-1px);
    border-color: ${theme.colors.border.orange};
  }

  &:active {
    transform: translateY(0);
  }
`;

export const QuickActionIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

export const QuickActionText = styled.span`
  flex: 1;
  text-align: left;
`;

// Navigation States
export const NavigationState = {
  HIDDEN: 'hidden',
  VISIBLE: 'visible',
  MINIMIZED: 'minimized',
} as const;

export type NavigationStateType = typeof NavigationState[keyof typeof NavigationState];
